/*
  RD GLOBAL PRODUCT HERO JS
  Covers section-level behavior for the modular product hero:
  - Variant selection, price updates, and variant image switching
  - Gallery thumbnail clicks, arrows, and mobile swipe
  - AJAX Add to Cart, add-on quick add, Buy Now, cart count refresh, and share
  - Complete The Look See more / Show less toggling
*/


(() => {

  /* Main controller for one product hero section instance. */
  class RDGlobalProductHero {
    constructor(root) {
      this.root = root;
      this.jsonEl = root.querySelector('[data-rd-ph-product-json]');
      if (!this.jsonEl) return;

      try {
        this.data = JSON.parse(this.jsonEl.textContent);
      } catch (error) {
        console.warn('RD product hero JSON error', error);
        return;
      }

      this.product = this.data.product || {};
      this.variants = this.product.variants || [];
      this.currentVariant = this.variants.find((variant) => Number(variant.id) === Number(this.data.selectedVariantId)) || this.variants[0];
      this.currency = this.data.currency || 'INR';
      this.compareAtPriceMax = Number(this.data.compareAtPriceMax || 0);
      this.routes = this.data.routes || { cartAdd: '/cart/add.js', cart: '/cart' };
      this.variantInventory = this.data.variantInventory || {};
      this.strings = this.data.strings || {};
      this.wishlist = this.data.wishlist || {};
      this.mediaItems = Array.from(root.querySelectorAll('[data-rd-ph-media]'));
      this.thumbs = Array.from(root.querySelectorAll('[data-rd-ph-thumb]'));
      this.galleryStage = root.querySelector('[data-rd-ph-stage]');
      this.thumbRail = root.querySelector('[data-rd-ph-thumbs]');
      this.activeMediaIndex = Math.max(0, this.mediaItems.findIndex((item) => item.classList.contains('is-active')));

      this.bindEvents();
      this.updateForVariant(this.currentVariant, false);
      this.syncThumbnailHeight();
      this.syncCompleteLookHeights();
      this.updateWishlistButtons();
    }


    /* Bind all click, change, form, and swipe events inside the section. */
    bindEvents() {
      this.root.addEventListener('change', (event) => {
        if (event.target.matches('[data-rd-ph-option-input]')) this.onOptionChange(event.target);
      });

      this.root.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-rd-ph-thumb]');
        if (thumb) this.activateMediaById(thumb.dataset.mediaId);

        if (event.target.closest('[data-rd-ph-prev-media]')) this.activateMediaByIndex(this.activeMediaIndex - 1);
        if (event.target.closest('[data-rd-ph-next-media]')) this.activateMediaByIndex(this.activeMediaIndex + 1);
        if (event.target.closest('[data-rd-ph-qty-minus]')) this.adjustQty(-1);
        if (event.target.closest('[data-rd-ph-qty-plus]')) this.adjustQty(1);
        if (event.target.closest('[data-rd-ph-buy-now]')) {
          event.preventDefault();
          this.buyNow();
          return;
        }
        if (event.target.closest('[data-rd-ph-sticky-buy]')) {
          event.preventDefault();
          this.buyNow();
          return;
        }
        if (event.target.closest('[data-rd-ph-sticky-atc]')) {
          event.preventDefault();
          this.submitMainForm();
          return;
        }

        const reviewJump = event.target.closest('[data-rd-ph-review-jump]');
        if (reviewJump) {
          const target = document.querySelector(reviewJump.getAttribute('href'));
          if (target) {
            event.preventDefault();
            this.scrollToWithOffset(target);
          }
          return;
        }

        const wishlistButton = event.target.closest('[data-rd-ph-wishlist]');
        if (wishlistButton) {
          event.preventDefault();
          event.stopPropagation();
          this.toggleWishlist(wishlistButton);
          return;
        }

        if (event.target.closest('[data-rd-ph-share]')) {
          event.preventDefault();
          this.shareProduct();
          return;
        }

        const completeToggle = event.target.closest('[data-rd-ph-complete-toggle]');
        if (completeToggle) this.toggleCompleteLook(completeToggle);

        const quickAdd = event.target.closest('[data-rd-ph-quick-add]');
        if (quickAdd) this.quickAdd(quickAdd.dataset.rdPhQuickAdd, quickAdd);
      });

      const form = this.root.querySelector('[data-rd-ph-product-form]');
      if (form) {
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.addToCart(form);
        });
      }

      this.bindSwipe();
      this.bindZoom();

      /* Keep desktop thumbnail rail exactly equal to the main image height. */
      window.addEventListener('resize', () => {
        this.syncThumbnailHeight();
        this.syncCompleteLookHeights();
      }, { passive: true });

      this.root.querySelectorAll('.rd-ph-gallery__media img').forEach((image) => {
        image.addEventListener('load', () => this.syncThumbnailHeight(), { passive: true });
      });

      this.root.querySelectorAll('.rd-ph-complete__image img').forEach((image) => {
        image.addEventListener('load', () => this.syncCompleteLookHeights(), { passive: true });
      });

    }


    /* Match the left thumbnail rail height to the right main media stage on desktop. */
    syncThumbnailHeight() {
      if (!this.galleryStage || !this.thumbRail) return;

      const desktop = window.matchMedia('(min-width: 750px)').matches;
      if (!desktop) {
        this.thumbRail.style.height = '';
        this.thumbRail.style.maxHeight = '';
        return;
      }

      window.requestAnimationFrame(() => {
        const stageHeight = Math.round(this.galleryStage.getBoundingClientRect().height);
        if (stageHeight > 0) {
          this.thumbRail.style.height = `${stageHeight}px`;
          this.thumbRail.style.maxHeight = `${stageHeight}px`;
        }
      });
    }


    /* Mobile gallery swipe support for the main media stage. */
    bindSwipe() {
      const stage = this.root.querySelector('[data-rd-ph-stage]');
      if (!stage || this.mediaItems.length < 2) return;

      let startX = null;
      let startY = null;

      stage.addEventListener('touchstart', (event) => {
        const touch = event.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
      }, { passive: true });

      stage.addEventListener('touchend', (event) => {
        if (startX === null || startY === null) return;
        const touch = event.changedTouches[0];
        const diffX = touch.clientX - startX;
        const diffY = touch.clientY - startY;
        if (Math.abs(diffX) > 45 && Math.abs(diffX) > Math.abs(diffY)) {
          this.activateMediaByIndex(this.activeMediaIndex + (diffX < 0 ? 1 : -1));
          /* Swallow the click that follows a swipe so it doesn't also toggle zoom. */
          this.recentlySwiped = true;
          window.setTimeout(() => { this.recentlySwiped = false; }, 300);
        }
        startX = null;
        startY = null;
      }, { passive: true });
    }


    /* Click-to-zoom (touch) and hover-to-zoom-follow-cursor (desktop) for the active gallery image. */
    bindZoom() {
      const stage = this.galleryStage;
      if (!stage) return;

      const isHoverCapable = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      const getActiveImage = () => stage.querySelector('.rd-ph-gallery__media.is-active img');

      const setZoomOrigin = (clientX, clientY) => {
        const image = getActiveImage();
        if (!image) return;
        const rect = image.getBoundingClientRect();
        const x = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
        const y = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));
        stage.style.setProperty('--rd-zoom-x', `${x}%`);
        stage.style.setProperty('--rd-zoom-y', `${y}%`);
      };

      /* Desktop: zoom follows the cursor while hovering, resets on leave. */
      stage.addEventListener('mouseenter', () => {
        if (!isHoverCapable() || !getActiveImage()) return;
        stage.classList.add('is-zoomed');
      });

      stage.addEventListener('mousemove', (event) => {
        if (!isHoverCapable() || !stage.classList.contains('is-zoomed')) return;
        setZoomOrigin(event.clientX, event.clientY);
      });

      stage.addEventListener('mouseleave', () => {
        if (!isHoverCapable()) return;
        stage.classList.remove('is-zoomed');
      });

      /* Touch: tap toggles a fixed zoom centered on the tap point. Ignored right after a swipe. */
      stage.addEventListener('click', (event) => {
        if (isHoverCapable() || this.recentlySwiped || !getActiveImage()) return;
        if (event.target.closest('[data-rd-ph-prev-media], [data-rd-ph-next-media], [data-rd-ph-thumb], [data-rd-ph-wishlist]')) return;

        if (stage.classList.contains('is-zoomed')) {
          stage.classList.remove('is-zoomed');
          return;
        }

        setZoomOrigin(event.clientX, event.clientY);
        stage.classList.add('is-zoomed');
      });
    }


    /* Clear any active zoom state, e.g. when the visible media changes. */
    resetZoom() {
      if (this.galleryStage) this.galleryStage.classList.remove('is-zoomed');
    }


    /* Read the currently checked product options from the custom variant picker. */
    selectedOptions() {
      const options = [];
      this.root.querySelectorAll('[data-rd-ph-option-input]:checked').forEach((input) => {
        options[Number(input.dataset.optionPosition) - 1] = input.value;
      });
      return options;
    }


    /* Match selected options to a Shopify variant and refresh the product state. */
    onOptionChange(input) {
      const label = this.root.querySelector(`[data-rd-ph-selected-option="${input.dataset.optionPosition}"]`);
      if (label) label.textContent = input.value;

      const selected = this.selectedOptions();
      const variant = this.variants.find((candidate) => {
        return candidate.options.every((option, index) => option === selected[index]);
      });

      this.currentVariant = variant || null;
      this.updateForVariant(this.currentVariant, true);
    }


    /* Update variant id, price, compare price, saving text, SKU, buttons, media, and URL. */
    updateForVariant(variant, updateUrl = true) {
      const variantInput = this.root.querySelector('[data-rd-ph-variant-id]');
      const priceEl = this.root.querySelector('[data-rd-ph-price]');
      const compareEl = this.root.querySelector('[data-rd-ph-compare]');
      const saveEl = this.root.querySelector('[data-rd-ph-save]');
      const skuEl = this.root.querySelector('[data-rd-ph-sku]');
      const stickyPrice = this.root.querySelector('[data-rd-ph-sticky-price]');
      const atcButtons = this.root.querySelectorAll('[data-rd-ph-add-to-cart], [data-rd-ph-sticky-atc], [data-rd-ph-buy-now], [data-rd-ph-sticky-buy]');
      const atcText = this.root.querySelector('[data-rd-ph-atc-text]');

      if (!variant) {
        if (variantInput) variantInput.value = '';
        atcButtons.forEach((button) => button.disabled = true);
        if (atcText) atcText.textContent = this.strings.unavailable || 'Unavailable';
        return;
      }

      if (variantInput) variantInput.value = variant.id;
      if (priceEl) priceEl.textContent = this.formatMoney(variant.price);
      if (stickyPrice) stickyPrice.textContent = this.formatMoney(variant.price);

      const fallbackCompare = this.compareAtPriceMax && this.compareAtPriceMax > variant.price ? this.compareAtPriceMax : 0;
      const activeCompare = variant.compare_at_price && variant.compare_at_price > variant.price ? variant.compare_at_price : fallbackCompare;
      const hasSaving = Boolean(activeCompare && activeCompare > variant.price);
      const manualCompare = this.strings.manualComparePrice || '';
      const manualSave = this.strings.manualSaveText || '';

      if (compareEl) {
        compareEl.textContent = hasSaving ? this.formatMoney(activeCompare) : manualCompare;
        compareEl.classList.toggle('is-hidden', !(hasSaving || manualCompare));
      }

      if (saveEl) {
        if (hasSaving) {
          const percent = Math.round(((activeCompare - variant.price) * 100) / activeCompare);
          saveEl.textContent = `(${this.strings.savePrefix || 'Save'} ${percent}%)`;
        } else if (manualSave) {
          saveEl.textContent = manualSave;
        }
        saveEl.classList.toggle('is-hidden', !(hasSaving || manualSave));
      }

      if (skuEl) skuEl.textContent = variant.sku ? `SKU: ${variant.sku}` : '';

      /* Keep the WhatsApp enquiry link pointed at the variant the shopper is actually looking at. */
      const waLink = this.root.querySelector('[data-rd-ph-whatsapp]');
      if (waLink) {
        const number = waLink.getAttribute('data-rd-ph-wa-number') || '';
        const template = waLink.getAttribute('data-rd-ph-wa-template') || '';
        if (number && template) {
          const body = template.replace('__SKU__', variant.sku || 'N/A');
          waLink.setAttribute('href', `https://wa.me/${number}?text=${encodeURIComponent(body)}`);
        }
      }

      const stockEl = this.root.querySelector('[data-rd-ph-stock]');
      if (stockEl) {
        const inventory = this.variantInventory[variant.id] || {};
        const quantity = Number(inventory.quantity);
        const threshold = Number(stockEl.dataset.rdPhStockThreshold || 10);
        const showStock = inventory.management === 'shopify' && quantity > 0 && quantity <= threshold;
        stockEl.classList.toggle('is-hidden', !showStock);
        const stockText = stockEl.querySelector('[data-rd-ph-stock-text]');
        if (stockText) stockText.textContent = `Only ${quantity} left in stock`;
      }

      atcButtons.forEach((button) => button.disabled = !variant.available);
      if (atcText) atcText.textContent = variant.available ? (this.strings.addToCart || 'Add to Cart') : (this.strings.soldOut || 'Sold Out');

      const mediaId = variant.featured_media && variant.featured_media.id;
      if (mediaId) this.activateMediaById(mediaId);

      if (updateUrl && window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      this.dispatch('rd-ph:variant-change', { variant });
    }


    /* Switch main gallery media by Shopify media id. */
    activateMediaById(mediaId) {
      const index = this.mediaItems.findIndex((item) => String(item.dataset.mediaId) === String(mediaId));
      if (index >= 0) this.activateMediaByIndex(index);
    }


    /* Switch main gallery media by zero-based index and sync active thumbnail state. */
    activateMediaByIndex(index) {
      if (!this.mediaItems.length) return;
      const nextIndex = (index + this.mediaItems.length) % this.mediaItems.length;
      this.activeMediaIndex = nextIndex;
      this.resetZoom();

      this.mediaItems.forEach((item, i) => {
        const active = i === nextIndex;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (!active) this.pauseMedia(item);
      });

      const activeId = this.mediaItems[nextIndex].dataset.mediaId;
      this.thumbs.forEach((thumb) => {
        const active = String(thumb.dataset.mediaId) === String(activeId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });

      this.syncThumbnailHeight();
      this.playActiveMedia();
    }

    pauseMedia(container) {
      container.querySelectorAll('video').forEach((video) => video.pause());
    }

    /* Auto-play the video on the newly-active slide, if any. Browsers can still
       block unmuted autoplay before the visitor has interacted with the page,
       so a rejected play() promise is expected and silently ignored. */
    playActiveMedia() {
      const active = this.mediaItems[this.activeMediaIndex];
      if (!active) return;
      const video = active.querySelector('video');
      if (!video) return;
      video.currentTime = 0;
      const playResult = video.play();
      if (playResult && typeof playResult.catch === 'function') {
        playResult.catch(() => {});
      }
    }

    adjustQty(delta) {
      const qty = this.root.querySelector('[data-rd-ph-quantity]');
      if (!qty) return;
      const current = Number(qty.value || 1);
      qty.value = Math.max(1, current + delta);
    }

    submitMainForm() {
      const form = this.root.querySelector('[data-rd-ph-product-form]');
      if (form) this.addToCart(form);
    }


    /* The theme's cart drawer/notification component, if the store uses one. */
    getCartComponent() {
      return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    }

    /* Main Add to Cart. Adds selected variant, then opens the cart drawer if the
       theme has one; otherwise falls back to redirecting to the cart page. */
    async addToCart(form) {
      if (!this.currentVariant || !this.currentVariant.available) return;

      const button = this.root.querySelector('[data-rd-ph-add-to-cart]');
      const buttonText = this.root.querySelector('[data-rd-ph-atc-text]');
      const stickyButton = this.root.querySelector('[data-rd-ph-sticky-atc]');
      const message = this.root.querySelector('[data-rd-ph-form-message]');
      const formData = new FormData(form);
      const cart = this.getCartComponent();

      if (cart) {
        formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
      }

      this.setLoading(true, button, buttonText);
      this.setStickyAtcState(stickyButton, 'loading');
      if (message) message.textContent = '';

      try {
        const response = await fetch(this.routes.cartAdd || '/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: formData
        });

        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Could not add to cart');

        if (message) message.textContent = this.strings.added || 'Added to cart';
        this.setStickyAtcState(stickyButton, 'success');
        this.dispatch('cart:updated', { item: result });
        this.dispatch('rd-ph:cart-added', { item: result });

        if (cart) {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(result);
        } else {
          await this.refreshCartCount();
          window.location.href = this.routes.cart || '/cart';
        }
      } catch (error) {
        if (message) message.textContent = error.message || 'Something went wrong';
        this.setStickyAtcState(stickyButton, 'default');
      } finally {
        this.setLoading(false, button, buttonText);
      }
    }


    /* Loading spinner / success checkmark feedback on the mobile sticky Add to Cart button. */
    setStickyAtcState(button, state) {
      if (!button) return;

      const icon = button.querySelector('[data-rd-ph-sticky-atc-icon]');
      const text = button.querySelector('[data-rd-ph-sticky-atc-text]');
      if (text && !button.dataset.defaultLabel) button.dataset.defaultLabel = text.textContent;

      if (button.rdPhStickyRevertTimer) {
        clearTimeout(button.rdPhStickyRevertTimer);
        button.rdPhStickyRevertTimer = null;
      }

      button.classList.remove('is-loading', 'is-success');

      if (state === 'loading') {
        button.disabled = true;
        if (icon) icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><circle cx="12" cy="12" r="9" stroke-opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9" stroke-linecap="round"/></svg>';
        if (text) text.textContent = this.strings.adding || 'Adding...';
        button.classList.add('is-loading');
      } else if (state === 'success') {
        button.disabled = true;
        if (icon) icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
        if (text) text.textContent = this.strings.added || 'Added';
        button.classList.add('is-success');
        button.rdPhStickyRevertTimer = setTimeout(() => {
          this.setStickyAtcState(button, 'default');
        }, 1400);
      } else {
        button.disabled = !(this.currentVariant && this.currentVariant.available);
        if (icon) icon.innerHTML = '';
        if (text) text.textContent = button.dataset.defaultLabel || (this.strings.addToCart || 'Add to Cart');
      }
    }


    /* Keep collapsed Complete The Look height to 1 full card + 40% of the next card. */
    syncCompleteLookHeights() {
      this.root.querySelectorAll('[data-rd-ph-complete]').forEach((module) => {
        const list = module.querySelector('.rd-ph-complete__list');
        if (!list) return;

        const visibleItems = Array.from(list.querySelectorAll('.rd-ph-complete__item')).filter((item) => !item.hidden);
        if (visibleItems.length < 2) {
          module.style.setProperty('--rd-ph-complete-collapsed-height', 'none');
          return;
        }

        const first = visibleItems[0];
        const second = visibleItems[1];
        const listTop = list.getBoundingClientRect().top;
        const firstRect = first.getBoundingClientRect();
        const secondRect = second.getBoundingClientRect();
        const peekRatio = Number.parseFloat(module.dataset.rdPhCompletePeek || '0.4');
        const safePeek = Number.isFinite(peekRatio) ? Math.min(Math.max(peekRatio, 0.2), 0.8) : 0.4;
        const collapsedHeight = (firstRect.bottom - listTop) + (secondRect.height * safePeek);

        module.style.setProperty('--rd-ph-complete-collapsed-height', `${Math.ceil(collapsedHeight)}px`);
      });
    }


    /* Expand/collapse extra Complete The Look add-ons. */
    toggleCompleteLook(button) {
      const module = button.closest('[data-rd-ph-complete]');
      if (!module) return;

      const expanded = button.getAttribute('aria-expanded') === 'true';
      const nextExpanded = !expanded;
      const moreText = button.dataset.moreText || 'See more';
      const lessText = button.dataset.lessText || 'Show less';

      module.querySelectorAll('[data-rd-ph-complete-extra]').forEach((item) => {
        item.hidden = !nextExpanded;
      });

      module.classList.toggle('is-expanded', nextExpanded);
      button.setAttribute('aria-expanded', nextExpanded ? 'true' : 'false');
      button.textContent = nextExpanded ? lessText : moreText;

      if (!nextExpanded) this.syncCompleteLookHeights();
    }


    /* Complete-the-look quick add handler. */
    async quickAdd(variantId, button) {
      if (!variantId || !button) return;
      const original = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding...';
      const cart = this.getCartComponent();
      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        if (cart) {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
        }
        const response = await fetch(this.routes.cartAdd || '/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: formData
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Error');
        button.textContent = 'Added';
        this.dispatch('cart:updated', { item: result });
        if (cart) {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(result);
        } else {
          await this.refreshCartCount();
        }
        setTimeout(() => { button.textContent = original; button.disabled = false; }, 1200);
      } catch (error) {
        button.textContent = 'Error';
        setTimeout(() => { button.textContent = original; button.disabled = false; }, 1200);
      }
    }

    setLoading(isLoading, button, buttonText) {
      if (!button) return;
      button.disabled = isLoading || !(this.currentVariant && this.currentVariant.available);
      if (buttonText) buttonText.textContent = isLoading ? (this.strings.adding || 'Adding...') : (this.currentVariant && this.currentVariant.available ? (this.strings.addToCart || 'Add to Cart') : (this.strings.soldOut || 'Sold Out'));
    }


    /* Direct checkout route for selected variant and quantity. */
    buyNow() {
      if (!this.currentVariant || !this.currentVariant.available) return;
      const qtyInput = this.root.querySelector('[data-rd-ph-quantity]');
      const quantity = Math.max(1, Number(qtyInput ? qtyInput.value : 1) || 1);
      window.location.href = `/cart/${encodeURIComponent(this.currentVariant.id)}:${quantity}?checkout`;
    }


    /* Refresh standard Dawn/cart-count elements without forcing a page reload. */
    async refreshCartCount() {
      try {
        const response = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        const cart = await response.json();
        const count = cart.item_count || 0;
        document.querySelectorAll('[data-cart-count], .cart-count-bubble span:first-child, #cart-icon-bubble .cart-count-bubble span:first-child').forEach((el) => {
          el.textContent = count;
        });
        const bubble = document.querySelector('.cart-count-bubble');
        if (bubble) bubble.classList.toggle('hidden', count === 0);
      } catch (error) {
        // Cart count refresh is non-critical.
      }
    }


    wishlistUrl() {
      return (this.wishlist && this.wishlist.url) || '/pages/wishlist';
    }


    wishlistHandle(button) {
      return (button && button.getAttribute('data-product-handle')) || (this.wishlist && this.wishlist.productHandle) || this.product.handle || '';
    }


    async getWishlist() {
      try {
        if (typeof window.tiffanyWishlistGetWishlist === 'function') {
          const list = await window.tiffanyWishlistGetWishlist();
          return Array.isArray(list) ? list : [];
        }

        const raw = window.localStorage.getItem('tiffany_wishlist');
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list : [];
      } catch (error) {
        return [];
      }
    }


    async saveWishlist(list) {
      const safeList = Array.isArray(list) ? [...new Set(list.filter(Boolean))] : [];

      try {
        if (typeof window.tiffanyWishlistSaveWishlist === 'function') {
          await window.tiffanyWishlistSaveWishlist(safeList);
        } else {
          window.localStorage.setItem('tiffany_wishlist', JSON.stringify(safeList));
        }
      } catch (error) {
        window.localStorage.setItem('tiffany_wishlist', JSON.stringify(safeList));
      }

      return safeList;
    }


    setWishlistButtonState(button, inWishlist) {
      if (!button) return;

      button.classList.toggle('in-wishlist', inWishlist);
      button.setAttribute('aria-pressed', inWishlist ? 'true' : 'false');

      const icon = button.querySelector('.rd-ph-actions__heart, i');
      if (icon) {
        if (icon.tagName && icon.tagName.toLowerCase() === 'i') {
          icon.classList.remove('fa-heart', 'fa-heart-o');
          icon.classList.add(inWishlist ? 'fa-heart' : 'fa-heart-o');
        } else {
          icon.textContent = inWishlist ? '♥' : '♡';
        }
      }

      const label = button.querySelector('[data-rd-ph-wishlist-label]');
      if (label) {
        label.textContent = inWishlist ? (this.strings.wishlistActive || 'Wishlisted') : (this.strings.wishlistText || label.getAttribute('data-default-label') || 'Wishlist');
      }
    }


    async updateWishlistButtons() {
      const buttons = Array.from(this.root.querySelectorAll('[data-rd-ph-wishlist]'));
      if (!buttons.length) return;

      buttons.forEach((button) => {
        const label = button.querySelector('[data-rd-ph-wishlist-label]');
        if (label && !label.getAttribute('data-default-label')) label.setAttribute('data-default-label', label.textContent.trim() || 'Wishlist');
      });

      if (!this.wishlist.isCustomerLoggedIn) {
        buttons.forEach((button) => this.setWishlistButtonState(button, false));
        return;
      }

      const list = await this.getWishlist();
      buttons.forEach((button) => {
        const handle = this.wishlistHandle(button);
        this.setWishlistButtonState(button, Boolean(handle && list.includes(handle)));
      });
    }


    /* Smooth-scroll to a target element, offsetting for the sticky announcement
       bar/header so the target doesn't land hidden underneath it. */
    scrollToWithOffset(target) {
      const header = document.querySelector('sticky-header, .header-wrapper, #shopify-section-header');
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
      window.scrollTo({ top, behavior: 'smooth' });
    }


    async toggleWishlist(button) {
      const handle = this.wishlistHandle(button);
      if (!handle) return;

      if (!this.wishlist.isCustomerLoggedIn) {
        window.location.href = this.wishlistUrl();
        return;
      }

      button.classList.add('is-wishlist-animating');

      const list = await this.getWishlist();
      const exists = list.includes(handle);
      const nextList = exists ? list.filter((item) => item !== handle) : [...list, handle];
      await this.saveWishlist(nextList);

      this.root.querySelectorAll('[data-rd-ph-wishlist]').forEach((candidate) => {
        if (this.wishlistHandle(candidate) === handle) this.setWishlistButtonState(candidate, !exists);
      });

      const message = this.root.querySelector('[data-rd-ph-form-message]');
      if (message) message.textContent = !exists ? (this.strings.wishlistAdded || 'Added to wishlist') : (this.strings.wishlistRemoved || 'Removed from wishlist');

      this.dispatch('wishlist:updated', { handle, wishlist: nextList, inWishlist: !exists });

      window.setTimeout(() => button.classList.remove('is-wishlist-animating'), 460);
    }


    /* Native share / copy link support. */
    async shareProduct() {
      const message = this.root.querySelector('[data-rd-ph-form-message]');
      const shareData = { title: document.title, url: window.location.href };
      try {
        if (navigator.share) {
          await navigator.share(shareData);
        } else if (navigator.clipboard) {
          await navigator.clipboard.writeText(window.location.href);
          if (message) message.textContent = this.strings.linkCopied || 'Link copied';
        }
      } catch (error) {
        // User cancelled share sheet.
      }
    }


    /* Format Shopify cent values using the store currency. */
    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      const noDecimals = Number(cents || 0) % 100 === 0;
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: noDecimals ? 0 : 2,
          maximumFractionDigits: noDecimals ? 0 : 2
        }).format(amount);
      } catch (error) {
        return `₹${noDecimals ? Math.round(amount) : amount.toFixed(2)}`;
      }
    }

    dispatch(name, detail) {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
      this.root.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
    }
  }


  /* Initialize all product hero sections, including Shopify theme editor reloads. */
  function initRDProductHeroes(container = document) {
    container.querySelectorAll('[data-rd-product-hero]').forEach((root) => {
      if (root.__rdProductHero) return;
      root.__rdProductHero = new RDGlobalProductHero(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initRDProductHeroes());
  } else {
    initRDProductHeroes();
  }

  document.addEventListener('shopify:section:load', (event) => initRDProductHeroes(event.target));
})();
